/**
 * The icon set, drawn here rather than installed.
 *
 * Twenty-odd glyphs is less code than a dependency and it buys the one thing an icon
 * package cannot: every stroke is 1.5 on a 24 grid and every corner is round, so the
 * icons sit at the same optical weight as the 13px UI text beside them. A mixed-weight
 * icon set is the single most reliable way to make a dashboard look assembled rather
 * than designed, and it is invisible until you put two of them in a row.
 *
 * Everything inherits `currentColor` and sizes off `--size`, so an icon is coloured and
 * scaled by the thing it sits inside and never carries its own palette.
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Svg({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative by default. Every icon in this app sits beside its own label, so
      // announcing it a second time is noise in a screen reader.
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* --- the wordmark ------------------------------------------------------------------ */

const PETAL_LEFT = "M50 46C39.5 34.8 35 29.8 35 21A15 15 0 0 1 50 6Z";
const PETAL_RIGHT = "M50 46C60.5 34.8 65 29.8 65 21A15 15 0 0 0 50 6Z";

/**
 * The Replystate flower: five petals, each folded into a full half and a lighter half,
 * turned into a swirl. The same mark as the website, LinkedIn and the favicon.
 *
 * One colour, `currentColor`, with the lighter half at 0.68 opacity. On the white ground
 * that lands exactly on the brand's second violet (#9a85f8 over #6a4cf5), and it lets the
 * empty state draw the same flower in grey without a second copy.
 */
export function Mark({ size = 22, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="8 5 89 89" fill="currentColor" aria-hidden="true" {...rest}>
      {[0, 72, 144, 216, 288].map((turn) => (
        <g key={turn} transform={`rotate(${turn} 50 50) rotate(18 50 46)`}>
          <path d={PETAL_LEFT} />
          <path d={PETAL_RIGHT} opacity="0.68" />
        </g>
      ))}
    </svg>
  );
}

/* --- navigation -------------------------------------------------------------------- */

export const Gauge = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 14.5 16 9" />
    <path d="M3.5 17a9 9 0 1 1 17 0" />
    <circle cx="12" cy="15.5" r="1.4" />
  </Svg>
);

export const Phone = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15.5 21c-6 0-12.5-6.5-12.5-12.5 0-1 .3-1.7 1-2.3l1.4-1.3c.6-.5 1.4-.5 1.9.1l2.2 2.6c.5.6.4 1.4-.1 1.9l-1 .9a12 12 0 0 0 4.7 4.7l.9-1c.5-.5 1.3-.6 1.9-.1l2.6 2.2c.6.5.6 1.3.1 1.9l-1.3 1.4c-.6.7-1.3 1-2.3 1Z" />
  </Svg>
);

/** A microphone, for starting a test call. */
export const Mic = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
  </Svg>
);

/** A call that came in through the browser rather than over the phone. */
export const Globe = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
  </Svg>
);

export const Stack = (p: IconProps) => (
  <Svg {...p}>
    <path d="m12 3 9 4.5-9 4.5-9-4.5L12 3Z" />
    <path d="m3.5 12.5 8.5 4.2 8.5-4.2" />
    <path d="m3.5 17 8.5 4.2 8.5-4.2" />
  </Svg>
);

export const Cog = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1h-.2a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5v-.2a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
  </Svg>
);

/* --- actions and affordances -------------------------------------------------------- */

export const Search = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

export const Filter = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 5h16l-6.2 7.3v5.9l-3.6 2v-7.9L4 5Z" />
  </Svg>
);

/** The + on an "add one of these" button. Never a standalone glyph; always beside a word. */
export const Plus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Svg>
);

/**
 * The x that dismisses a thing, and NOT the multiplication sign it replaced.
 *
 * The category editor used a literal `\u00d7` in a span, which inherits the UI face and
 * sits at whatever weight Inter draws it - beside a 1.5-stroke icon set it reads as a
 * character that wandered in from the text. This is two strokes on the same grid as the
 * other twenty-seven.
 */
export const Close = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 6 12 12" />
    <path d="m18 6-12 12" />
  </Svg>
);

export const ChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9.5 6 6 6-6" />
  </Svg>
);

export const ChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9.5 5 7 7-7 7" />
  </Svg>
);

export const ChevronLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="m14.5 5-7 7 7 7" />
  </Svg>
);

export const ArrowLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19 12H5" />
    <path d="m11 6-6 6 6 6" />
  </Svg>
);

export const Copy = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="9" width="11.5" height="11.5" rx="2.5" />
    <path d="M5.5 15H5a1.5 1.5 0 0 1-1.5-1.5V5A1.5 1.5 0 0 1 5 3.5h8.5A1.5 1.5 0 0 1 15 5v.5" />
  </Svg>
);

export const Download = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5v11" />
    <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
    <path d="M4 17.5v1A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5v-1" />
  </Svg>
);

export const SignOut = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 21H6a2.5 2.5 0 0 1-2.5-2.5v-13A2.5 2.5 0 0 1 6 3h3" />
    <path d="M16 16.5 20.5 12 16 7.5" />
    <path d="M20 12H9" />
  </Svg>
);

/**
 * A bare tick, which is not the same glyph as `Check` below.
 *
 * `Check` is circled and belongs to the vocabulary of a call - an outcome, a badge, a
 * thing that happened. This is a menu's "you are here" mark, and a circled tick at 15px
 * beside a row of text reads as a status rather than as a selection.
 */
export const Tick = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Svg>
);

/* --- the vocabulary of a call ------------------------------------------------------- */

export const Check = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.5 12.2 2.4 2.4 4.6-5" />
  </Svg>
);

export const Alert = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10.7 4.1 3.2 17a1.5 1.5 0 0 0 1.3 2.3h15a1.5 1.5 0 0 0 1.3-2.3L13.3 4.1a1.5 1.5 0 0 0-2.6 0Z" />
    <path d="M12 9.5v4" />
    <path d="M12 17h.01" />
  </Svg>
);

export const Clock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </Svg>
);

export const Calendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 10h17" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
  </Svg>
);

/** A calendar with a cross in it: days a location is closed. */
export const CalendarX = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 12V7.5A2.5 2.5 0 0 0 18 5H6a2.5 2.5 0 0 0-2.5 2.5V18A2.5 2.5 0 0 0 6 20.5h6" />
    <path d="M3.5 10h17" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
    <path d="m16 16 4 4" />
    <path d="m20 16-4 4" />
  </Svg>
);

export const User = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="3.8" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </Svg>
);

export const Pin = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.6" />
  </Svg>
);

export const Tag = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 11V5a1.5 1.5 0 0 1 1.5-1.5h6l9.5 9.5a1.5 1.5 0 0 1 0 2.1l-5.4 5.4a1.5 1.5 0 0 1-2.1 0L3.5 11Z" />
    <path d="M8 8h.01" />
  </Svg>
);

export const Sparkle = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5 13.7 9 19 10.7 13.7 12.4 12 18l-1.7-5.6L5 10.7 10.3 9 12 3.5Z" />
    <path d="M18.5 17.5 19 19l1.5.5L19 20l-.5 1.5L18 20l-1.5-.5L18 19l.5-1.5Z" />
  </Svg>
);

export const Quote = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 12.5a7.5 7.5 0 0 1-10.9 6.7L4 20.5l1.4-5.4A7.5 7.5 0 1 1 20.5 12.5Z" />
  </Svg>
);

export const Route = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="6" cy="5.5" r="2.5" />
    <circle cx="18" cy="18.5" r="2.5" />
    <path d="M8.5 5.5H14a3.5 3.5 0 0 1 0 7h-4a3.5 3.5 0 0 0 0 7h5.5" />
  </Svg>
);

export const List = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 6h11" />
    <path d="M9 12h11" />
    <path d="M9 18h11" />
    <path d="M4.5 6h.01" />
    <path d="M4.5 12h.01" />
    <path d="M4.5 18h.01" />
  </Svg>
);

export const Info = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </Svg>
);

export const Mail = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="m3.6 6.8 7.5 5.2a1.5 1.5 0 0 0 1.8 0l7.5-5.2" />
  </Svg>
);

/* --- the three moods ---------------------------------------------------------------- *
 *
 * A FACE EACH, AND THEY DIFFER ONLY IN THE MOUTH. The eyes and the ring are identical
 * across the three, so the row reads as one control with three states rather than as
 * three unrelated glyphs - and the mouth is the whole of the difference, which is what
 * makes them legible at 17px where a subtler distinction would not survive.
 *
 * They are the only icons here that carry meaning rather than decoration: on the
 * sentiment cards the face and the word say the same thing, so `aria-hidden` from `Svg`
 * is still correct.
 */

const Face = ({ children, ...p }: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9 10h.01" />
    <path d="M15 10h.01" />
    {children}
  </Svg>
);

export const Smile = (p: IconProps) => (
  <Face {...p}>
    <path d="M8.2 14a4.5 4.5 0 0 0 7.6 0" />
  </Face>
);

export const Meh = (p: IconProps) => (
  <Face {...p}>
    <path d="M8.5 14.5h7" />
  </Face>
);

export const Frown = (p: IconProps) => (
  <Face {...p}>
    <path d="M8.2 15.5a4.5 4.5 0 0 1 7.6 0" />
  </Face>
);

/* --- editing ------------------------------------------------------------------------ */

/** The affordance on a card that opens its dialog. Beside the word "Edit", never alone. */
export const Pencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20h4L19 9a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5z" />
    <path d="M14.5 6.5 17.5 9.5" />
  </Svg>
);

/* --- listening ----------------------------------------------------------------------- */

/**
 * A triangle, drawn as a closed path rather than as a `<polygon>` so it takes the same
 * 1.5 stroke and round joins as everything else here.
 *
 * ALONE ON THE TRANSPORT BUTTON, and nowhere else. A play triangle is one of the three or
 * four glyphs a person reads without a label - and the button it sits on is round, sits
 * against a scrubber and a clock, and says what it is by being all of that at once.
 */
export const Play = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7.5 5.6 18.2 11.6a.5.5 0 0 1 0 .8L7.5 18.4a.5.5 0 0 1-.75-.4V6a.5.5 0 0 1 .75-.4Z" />
  </Svg>
);

/**
 * Its other half, and the only icon here drawn at a weight of its own: two 1.5 strokes
 * this far apart read as a pause on a 24 grid, but not against a solid triangle beside
 * them, so the bars are filled rather than stroked and the two glyphs weigh the same.
 */
export const Pause = (p: IconProps) => (
  <Svg {...p}>
    <rect x="7.5" y="5.5" width="3.4" height="13" rx="1.2" fill="currentColor" stroke="none" />
    <rect x="13.1" y="5.5" width="3.4" height="13" rx="1.2" fill="currentColor" stroke="none" />
  </Svg>
);

/**
 * Make it again: two arrows chasing each other round a circle.
 *
 * The arcs stop short of the arrowheads rather than meeting them, which is what keeps the
 * gap legible at 15px - a closed ring with two ticks on it reads as a circle.
 */
export const Redo = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 12a8.5 8.5 0 0 1 14.4-6.1L20.5 8.3" />
    <path d="M20.5 4.2v4.1h-4.1" />
    <path d="M20.5 12a8.5 8.5 0 0 1-14.4 6.1L3.5 15.7" />
    <path d="M3.5 19.8v-4.1h4.1" />
  </Svg>
);

/** Throw the file away. The lid is a separate stroke so the glyph survives at 15px. */
/** A document with a folded corner: an uploaded file on the Knowledge tab. */
export const Doc = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3H6v18h12V7z" />
    <path d="M14 3v4h4" />
  </Svg>
);

/** Three lines of text: something the owner wrote. */
export const Lines = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 6h14M5 11h14M5 16h9" />
  </Svg>
);

/** One arrow round: read a page again now. */
export const Refresh = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 11a8 8 0 1 0-2.3 5.7" />
    <path d="M20 4v7h-7" />
  </Svg>
);

export const Trash = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.75 6.25h16.5" />
    <path d="M8.75 6.25V4.9a1.4 1.4 0 0 1 1.4-1.4h3.7a1.4 1.4 0 0 1 1.4 1.4v1.35" />
    <path d="M5.9 6.25 6.8 19.1a1.9 1.9 0 0 0 1.9 1.75h6.6a1.9 1.9 0 0 0 1.9-1.75l.9-12.85" />
    <path d="M10.2 10.4v6.1M13.8 10.4v6.1" />
  </Svg>
);
